import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { uploadExcelService } from "../helper";

export const getInitialMaterialOption = () => [
  { type: "", amount: "", note: "" },
];

const normalizeMaterialOptionForForm = (materialOption) => {
  if (Array.isArray(materialOption)) {
    if (materialOption.length === 0) {
      return getInitialMaterialOption();
    }
    return materialOption.map((item) => ({
      type: item?.type || "",
      amount: item?.amount != null ? String(item.amount) : "",
      note: item?.note || item?.notes || "",
    }));
  }

  if (materialOption && typeof materialOption === "object") {
    const rows = Object.entries(materialOption)
      .filter(
        ([, value]) =>
          value &&
          (String(value.amount || "").trim() ||
            String(value.note || value.notes || "").trim())
      )
      .map(([key, value]) => ({
        type: key,
        amount: value?.amount != null ? String(value.amount) : "",
        note: value?.note || value?.notes || "",
      }));

    return rows.length > 0 ? rows : getInitialMaterialOption();
  }

  return getInitialMaterialOption();
};

const normalizeMaterialOptionForAPI = (materialOption) => {
  const rows = Array.isArray(materialOption) ? materialOption : [];

  return rows
    .filter(
      (row) =>
        String(row?.type || "").trim() ||
        String(row?.amount || "").trim() ||
        String(row?.note || "").trim()
    )
    .map((row) => ({
      type: String(row.type || "").trim(),
      amount: String(row.amount ?? ""),
      note: String(row.note || "").trim(),
    }));
};

const normalizeDateForApi = (dateValue) => {
  if (!dateValue) return null;
  return dayjs(dateValue).format("YYYY-MM-DD");
};

const normalizeDateForForm = (dateValue) => {
  if (!dateValue) return null;
  return dayjs(dateValue).format("YYYY-MM-DD");
};

export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length === 0) {
          reject("Excel file is empty");
          return;
        }

        // Find column indices by looking for keywords in first row
        const headerRow = jsonData[0];
        const locationNameColumnIndex = headerRow.findIndex(cell =>
          cell && String(cell).toLowerCase().includes("location name")
        );
        const locationAddressColumnIndex = headerRow.findIndex(cell =>
          cell && String(cell).toLowerCase().includes("location address")
        );
        const locationNumberColumnIndex = headerRow.findIndex(cell =>
          cell && String(cell).toLowerCase().includes("location number")
        );
        const instructionsColumnIndex = headerRow.findIndex(cell =>
          cell && String(cell).toLowerCase().includes("instructions")
        );

        // If columns are not found by keywords, use first three columns as default
        const locationNameCol = locationNameColumnIndex !== -1 ? locationNameColumnIndex : 0;
        const locationAddressCol =
          locationAddressColumnIndex !== -1 ? locationAddressColumnIndex : 1;
        const locationNumberCol =
          locationNumberColumnIndex !== -1 ? locationNumberColumnIndex : 2;
        const instructionsCol =
          instructionsColumnIndex !== -1 ? instructionsColumnIndex : 3;

        // Extract location subtasks from all rows
        const subtasks = jsonData
          .slice(1) // Skip header row
          .filter(
            row =>
              row[locationNameCol] ||
              row[locationAddressCol] ||
              row[locationNumberCol] ||
              row[instructionsCol]
          )
          .map(row => ({
            locationName: String(row[locationNameCol] || "").trim(),
            locationAddress: String(row[locationAddressCol] || "").trim(),
            locationNumber: String(row[locationNumberCol] || "").trim(),
            instructions: String(row[instructionsCol] || "").trim(),
          }));

        resolve({ subtasks });
      } catch (error) {
        reject("Excel file parse error");
      }
    };
    
    reader.onerror = () => reject("File read error");
    reader.readAsArrayBuffer(file);
  });
};

export const handleFileUpload = async (
  file,
  currentForm,
  setForm,
  setIsLoading,
  openSnackAlert
) => {
  try {
    setIsLoading(true);

    // Parse Excel file to extract subtasks
    const parsedData = await parseExcelFile(file);

    // Upload file to cloudinary
    const uploadRes = await uploadExcelService(file);

    // Update form with parsed data
    setForm({
      ...currentForm,
      attachments: uploadRes,
      subTasks: parsedData.subtasks || [],
    });

    setIsLoading(false);
    openSnackAlert(
      `Excel file uploaded successfully! Found ${parsedData.subtasks.length} location rows.`,
      "success"
    );

    return true;
  } catch (error) {
    openSnackAlert(error, "error");
    setIsLoading(false);
    return false;
  }
};

export const transformTaskDataForForm = (task) => {
  return {
    employeeOrTeam: Array.isArray(task.worker) ? task.worker.map(w => w._id || w) : (task.worker?._id || task.worker || ""),
    taskType: task.type || "",
    taskCategory: task.taskType || "snowRemoval",
    title: task.name || "",
    description: task.description || "",
    priority: task.priority || "",
    startDate: normalizeDateForForm(task.startDate),
    endDate: normalizeDateForForm(task.endDate),
    notifyEmail:
      task.isNotifiedEmail !== undefined ? task.isNotifiedEmail : true,
    notifyPush: task.isPushNotified !== undefined ? task.isPushNotified : true,
    attachments: task.attachment || null,
    taskImages: task.taskImages || [],
    subTasks: task.subTask || [],
    materialOption: normalizeMaterialOptionForForm(task.materialOption),
  };
};

export const transformFormDataForAPI = (form, userId) => {
  const isSnowRemoval = (form.taskCategory || "snowRemoval") === "snowRemoval";

  const payload = {
    worker: Array.isArray(form.employeeOrTeam) ? form.employeeOrTeam : [form.employeeOrTeam],
    type: form.taskType,
    taskType: form.taskCategory || "snowRemoval",
    assignedBy: userId,
    name: form.title,
    priority: form.priority,
    startDate: normalizeDateForApi(form.startDate),
    endDate: normalizeDateForApi(form.endDate),
    description: form.description,
    subTask: isSnowRemoval ? form.subTasks || [] : [],
    taskImages: form.taskImages || [],
    isNotifiedEmail: true,
    isPushNotified: true,
  };

  if (isSnowRemoval && form.attachments) {
    payload.attachment = form.attachments;
  }

  if (!isSnowRemoval) {
    payload.materialOption = normalizeMaterialOptionForAPI(form.materialOption);
  }

  return payload;
};

export const getInitialFormState = () => ({
  employeeOrTeam: [],
  taskType: "",
  taskCategory: "snowRemoval",
  title: "",
  description: "",
  priority: "",
  startDate: null,
  endDate: null,
  notifyEmail: true,
  notifyPush: true,
  attachments: null,
  taskImages: [],
  subTasks: [],
  materialOption: getInitialMaterialOption(),
});
